import axios from 'axios';

const instance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true, // ✅ send cookies (includes refresh token)
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Attach access token to every request
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle expired tokens
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 403 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            return instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const userRole = localStorage.getItem('role'); // 'admin' or 'staff'

      try {
        // ✅ Backend will read the refresh token from cookie
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = response.data.accessToken;
        localStorage.setItem('token', newToken);
        console.log(newToken)

        processQueue(null, newToken);

        originalRequest.headers.Authorization = 'Bearer ' + newToken;
        return instance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        redirectToLogin(userRole);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function redirectToLogin(role) {
  localStorage.clear();
  if (role === 'admin') {
    window.location.href = '/platformadmin/login';
  } else {
    window.location.href = '/login';
  }
}

// API methods for agreements and consent forms
export const agreementAPI = {
  createAgreement: (data) => instance.post('/agreements', data),
  getAllAgreements: () => instance.get('/agreements'),
  getPatientAgreements: (patientId) => instance.get(`/agreements/patient/${patientId}`),
  getAgreementById: (id) => instance.get(`/agreements/${id}`),
  uploadDocuments: (agreementId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('documents', file));
    return instance.post(`/agreements/${agreementId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  attachConsentForm: (agreementId, consentFormId) =>
    instance.post(`/agreements/${agreementId}/consent`, { consent_form_id: consentFormId }),
  verifyAccessCode: (accessCode) => instance.post('/agreements/verify-access', { access_code: accessCode }),
  signAgreement: (agreementId, signatureData, accessCode) =>
    instance.post(`/agreements/${agreementId}/sign`, { signature_data: signatureData, access_code: accessCode }),
  getCompanyInfo: () => instance.get('/company')
};

export const staffAPI = {
  getAllStaff: () => instance.get('/staff/all')
};

export const consentAPI = {
  createConsentForm: (data) => instance.post('/agreements/consent-forms', data),
  getAllConsentForms: (status) => instance.get('/agreements/consent-forms', { params: { status } }),
  getConsentFormById: (id) => instance.get(`/agreements/consent-forms/${id}`),
  updateConsentForm: (id, data) => instance.put(`/agreements/consent-forms/${id}`, data),
  deleteConsentForm: (id) => instance.delete(`/agreements/consent-forms/${id}`),
  signConsentForm: (data) => instance.post('/agreements/consent-forms/sign', data),
  getPatientSignatures: (patientId) => instance.get(`/agreements/consent-signatures/patient/${patientId}`)
};

export const allocationAPI = {
  createAllocation: (data) => instance.post('/allocations', data),
  getAllocations: (patientId) => instance.get(`/allocations/${patientId}`),
  deactivateAllocation: (id) => instance.put(`/allocations/${id}/deactivate`)
};

export default instance;
